import type { Booth, PaymentItem } from "@fiket/booth-schema";
import { hasVideoItem, isValidKrw, photoPaymentActive, resolveVideoPayment, videoPaymentActive } from "@fiket/booth-schema";
import type { Route } from "../../store/router";

/**
 * 결제 분기 규칙 — 순수 함수 (vitest). 결제 스테이지는 항상 **영상 생성 직전**(사진만이면 printShare 직전) 에 끼운다 (스펙 §3).
 * 사진·영상 결제는 스위치가 따로다 (BOOTH-80): 사진은 기본 OFF 라 결제 QR 은 보통 영상 생성 직전에만 나온다.
 * 활성 판정은 서버 게이트와 같은 스키마 함수 — 세션 생성·agent 인쇄 = `photoPaymentActive`, 영상 시작 = `videoPaymentActive`.
 * 둘이 다른 함수를 쓰면 키오스크는 무료로 보내고 서버는 402 를 내는 식으로 어긋난다 (Ruling 16).
 */
export interface BoothPaymentState {
  /** 사진 결제가 걸린다 — "사진 받기"(결과 화면·영상 소개 "사진만") 가 payment 를 거치고, 영상 생성 직전 결제에 사진 항목이 들어간다 */
  photoActive: boolean;
  /** 영상 결제가 걸린다 — 영상 생성 직전 결제에 영상 항목이 들어간다 */
  videoActive: boolean;
  /** 영상 결제 ON 인데 영상 가격이 없다 (F1 fail-closed) — 결과 화면의 영상 CTA 를 숨긴다. 사진은 가격이 없으면 그냥 무료 */
  videoBlocked: boolean;
  /** 결과 화면 가격 라벨용. photoActive 가 아니면 null */
  photoKrw: number | null;
  /** 영상 가격. videoActive 일 때만 */
  videoKrw: number | null;
}

type PaymentBooth = Pick<Booth, "photoPaymentEnabled" | "videoPaymentEnabled" | "videoEnabled" | "pricing">;

export const INACTIVE_PAYMENT: BoothPaymentState = { photoActive: false, videoActive: false, videoBlocked: false, photoKrw: null, videoKrw: null };

export function boothPaymentState(booth: PaymentBooth | null | undefined): BoothPaymentState {
  if (!booth) return INACTIVE_PAYMENT;
  const photoActive = photoPaymentActive(booth);
  const videoActive = videoPaymentActive(booth);
  return {
    photoActive,
    videoActive,
    // 영상 결제 ON 인데 영상 가격이 없으면 영상은 막는다. 영상 결제 OFF 면 영상은 무료(S3)라 막지 않는다.
    videoBlocked: resolveVideoPayment(booth) && !isValidKrw(booth.pricing?.videoKrw),
    photoKrw: photoActive ? booth.pricing!.photoKrw! : null,
    videoKrw: videoActive ? booth.pricing!.videoKrw! : null,
  };
}

/** 사진·영상 어느 쪽이든 결제가 걸리는가 — 영상 생성 직전 결제 진입 판정 */
export function anyPaymentActive(pay: Pick<BoothPaymentState, "photoActive" | "videoActive">): boolean {
  return pay.photoActive || pay.videoActive;
}

/** 결과 화면 "사진 받기"·영상 소개 "사진만 할게요" 가 가는 곳 — 사진 결제가 걸릴 때만 payment */
export function receiveRoute(photoActive: boolean): Route {
  return photoActive ? "payment" : "printShare";
}

/** 동작 선택 "다음"·(동작 2개 미만일 때) 소개 "만들기" 가 가는 곳 — 사진·영상 어느 쪽이든 결제가 걸리면 영상 생성 직전에 결제 */
export function videoGenerateEntryRoute(anyActive: boolean): Route {
  return anyActive ? "payment" : "videoGenerate";
}

/** paid 확인 뒤 */
export function afterPaidRoute(withVideo: boolean): Route {
  return withVideo ? "videoGenerate" : "printShare";
}

/** 결제 항목에 영상이 실제로 포함됐나 — 환불 안내·재시도 게이트·결제 화면 안내 문구가 본다 (사진 ON + 영상 결제 OFF 면 영상 생성 전 결제에도 영상 항목이 없다) */
export function videoCharged(items: readonly PaymentItem[]): boolean {
  return hasVideoItem(items);
}

/** 취소·만료·서버 종결 → 결과 화면 (스펙 §3 "취소 · 만료 → review") */
export const PAYMENT_EXIT_ROUTE: Route = "review";

/**
 * 결제 생성(POST /api/payments) 실패 코드별 갈 곳. null = 에러 화면(다시 시도 / 처음으로).
 * - PAYMENT_DISABLED: 서버는 이 구성에 결제 항목이 없는데 키오스크 config 가 낡음(F5 역방향) → 무료로 다음 화면
 * - PRICING_MISSING: 영상 가격이 빠짐(F1) → 호출측이 config 를 재조회한 뒤 영상이면 결과 화면(CTA 가 사라진다), 사진이면 무료 수령
 */
export function createFailureRoute(code: string, withVideo: boolean): Route | null {
  switch (code) {
    case "PAYMENT_DISABLED": return afterPaidRoute(withVideo);
    case "PRICING_MISSING": return withVideo ? "review" : "printShare";
    default: return null;
  }
}
